import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Beaker,
  Play,
  Save,
  History,
  Sparkles,
  AlertCircle,
  CheckCircle,
  Copy,
  Settings,
  FileText,
  Cpu,
  DollarSign,
  Clock,
  Hash,
  TrendingUp,
  TrendingDown,
  Loader2
} from 'lucide-react';
import { getTestCases, getTestSessions } from '@/services/aiTestingService';
import { createComponentLogger } from '@/utils/logger';
import QueryTester from './QueryTester';
import ModelComparison from './ModelComparison';
import TestCaseManager from './TestCaseManager';
import ResponseHistory from './ResponseHistory';
import type { TestCase, TestSession } from '@/types/ai-admin';

const log = createComponentLogger('TestConsole', 'ai-testing');

export default function TestConsole() {
  const [activeTab, setActiveTab] = useState('query');
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [currentSession, setCurrentSession] = useState<TestSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [stats, setStats] = useState({
    totalTests: 0,
    avgResponseTime: 0,
    totalCost: 0,
    successRate: 0,
    modelUsage: {
      haiku: 0,
      sonnet: 0,
      opus: 0
    }
  });

  // Load test data from Supabase
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [casesData, sessionsData] = await Promise.all([
        getTestCases(),
        getTestSessions(50)
      ]);

      setTestCases(casesData);
      setSessions(sessionsData);
      calculateStats(sessionsData);

      log.info('Test data loaded', {
        testCases: casesData.length,
        sessions: sessionsData.length
      });
    } catch (error) {
      log.error('Failed to load test data', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (testSessions: TestSession[]) => {
    if (testSessions.length === 0) {
      return;
    }

    let totalResponseTime = 0;
    let totalCostSum = 0;
    let successCount = 0;
    const modelCount = { haiku: 0, sonnet: 0, opus: 0 };

    testSessions.forEach(session => {
      session.responses.forEach(response => {
        totalResponseTime += response.responseTime;
        totalCostSum += response.cost;
        if (response.success) {
successCount++;
}

        const modelName = response.model.toLowerCase();
        if (modelName.includes('haiku')) {
modelCount.haiku++;
} else if (modelName.includes('sonnet')) {
modelCount.sonnet++;
} else if (modelName.includes('opus')) {
modelCount.opus++;
}
      });
    });

    const totalResponses = testSessions.reduce((sum, s) => sum + s.responses.length, 0);

    setStats({
      totalTests: testSessions.length,
      avgResponseTime: totalResponseTime / (totalResponses || 1),
      totalCost: totalCostSum,
      successRate: (successCount / (totalResponses || 1)) * 100,
      modelUsage: modelCount
    });
  };

  // Handle new test session
  const handleNewSession = (session: TestSession) => {
    const newSessions = [...sessions, session];
    setSessions(newSessions);
    setCurrentSession(session);
    calculateStats(newSessions);
    // Session is already saved to database by the component that creates it
  };

  // Handle test case update
  const handleTestCaseUpdate = (updatedCase: TestCase) => {
    const newTestCases = testCases.map(tc =>
      tc.id === updatedCase.id ? updatedCase : tc
    );
    setTestCases(newTestCases);
    // Update is handled by TestCaseManager via service
  };

  // Handle test case creation
  const handleTestCaseCreate = (newCase: TestCase) => {
    const newTestCases = [...testCases, newCase];
    setTestCases(newTestCases);
    // Creation is handled by TestCaseManager via service
  };

  // Handle test case deletion
  const handleTestCaseDelete = (caseId: string) => {
    const newTestCases = testCases.filter(tc => tc.id !== caseId);
    setTestCases(newTestCases);
    // Deletion is handled by TestCaseManager via service
  };

  // Run all test cases - delegates to Query Testing tab
  const runAllTests = () => {
    // Switch to query testing tab where actual test execution happens
    setActiveTab('query');
    log.info('Switched to query testing tab for test execution');
  };

  // Export test results
  const exportResults = () => {
    const exportData = {
      testCases,
      sessions,
      stats,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${ encodeURIComponent(dataStr)}`;
    const exportFileDefaultName = `ai_test_results_${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">Test Console</h2>
          <p className="text-muted-foreground">
            Test queries, compare models, and validate AI responses
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={runAllTests}
            disabled={testCases.length === 0}
          >
            {isTestRunning ? (
              <>
                <Sparkles className="mr-2 h-4 w-4 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run All Tests
              </>
            )}
          </Button>
          <Button variant="outline" onClick={exportResults}>
            <Save className="mr-2 h-4 w-4" />
            Export Results
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
            <Beaker className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalTests}</div>
            <p className="text-xs text-muted-foreground">
              Test sessions run
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(stats.avgResponseTime / 1000).toFixed(1)}s
            </div>
            <p className="text-xs text-muted-foreground">
              Average response time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            {stats.successRate >= 90 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-orange-600" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Passing tests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Test Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.totalCost.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground">
              Total testing cost
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Model Usage</CardTitle>
            <Cpu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Badge variant="secondary" className="text-xs">
                H: {stats.modelUsage.haiku}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                S: {stats.modelUsage.sonnet}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                O: {stats.modelUsage.opus}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert for test status */}
      {currentSession && (
        <Alert className={currentSession.successRate >= 90 ? 'border-green-200' : 'border-orange-200'}>
          {currentSession.successRate >= 90 ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-orange-600" />
          )}
          <AlertDescription>
            Last test session completed with {currentSession.successRate.toFixed(1)}% success rate.
            {currentSession.responses.length} queries tested in {(currentSession.totalDuration / 1000).toFixed(1)} seconds.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="query">Query Testing</TabsTrigger>
          <TabsTrigger value="comparison">Model Comparison</TabsTrigger>
          <TabsTrigger value="cases">Test Cases</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="query" className="space-y-4">
          <QueryTester
            testCases={testCases}
            onSessionComplete={handleNewSession}
          />
        </TabsContent>

        <TabsContent value="comparison" className="space-y-4">
          <ModelComparison
            testCases={testCases}
            onSessionComplete={handleNewSession}
          />
        </TabsContent>

        <TabsContent value="cases" className="space-y-4">
          <TestCaseManager
            testCases={testCases}
            onUpdate={handleTestCaseUpdate}
            onCreate={handleTestCaseCreate}
            onDelete={handleTestCaseDelete}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <ResponseHistory
            sessions={sessions}
            testCases={testCases}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}